import { cn } from "@loot-os/ui/lib/utils";
import { ArrowLeftIcon, ArrowRightIcon } from "lucide-react";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

/**
 * A horizontal carousel built on native scroll snapping rather than a carousel library: the
 * design needs paging arrows and a "Slide n of m" readout, and nothing else. Native scrolling
 * keeps the touch, trackpad and keyboard behaviour the browser already gives us, and the
 * snap points double as the slide index — so there is no animation loop and no second source
 * of truth for which slide is showing.
 *
 * Both shapes in the design use it: the hero, whose items are full width, and the card rows,
 * whose items are fixed width and overflow.
 */
interface CarouselState {
  scrollerRef: React.RefObject<HTMLDivElement | null>;
  activeIndex: number;
  slideCount: number;
  canScrollPrev: boolean;
  canScrollNext: boolean;
  scrollBy: (direction: -1 | 1) => void;
  registerSlideCount: (count: number) => void;
}

const CarouselContext = createContext<CarouselState | null>(null);

/** Throws rather than returning null, so a stray part outside a <Carousel> fails loudly. */
function useCarousel(): CarouselState {
  const context = useContext(CarouselContext);
  if (!context) throw new Error("Carousel parts must be rendered inside <Carousel>");
  return context;
}

function Carousel({ className, children, ...props }: React.ComponentProps<"div">) {
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [slideCount, setSlideCount] = useState(0);
  const [canScrollPrev, setCanScrollPrev] = useState(false);
  const [canScrollNext, setCanScrollNext] = useState(false);

  // Read straight off the scroller: which slide is showing is wherever the browser settled, and
  // the 1px slack absorbs the sub-pixel scrollLeft a fractional layout produces at either end.
  const measure = useCallback(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const { scrollLeft, clientWidth, scrollWidth } = scroller;
    setCanScrollPrev(scrollLeft > 1);
    setCanScrollNext(scrollLeft + clientWidth < scrollWidth - 1);

    const slides = Array.from(scroller.children) as HTMLElement[];
    let nearest = 0;
    let shortest = Number.POSITIVE_INFINITY;
    for (const [index, slide] of slides.entries()) {
      const distance = Math.abs(slide.offsetLeft - scroller.offsetLeft - scrollLeft);
      if (distance < shortest) {
        shortest = distance;
        nearest = index;
      }
    }
    setActiveIndex(nearest);
  }, []);

  // Re-runs on slideCount, which is what makes the arrows come alive once a query resolves:
  // slides arriving change the scroller's scrollWidth but not its own box, so the ResizeObserver
  // alone would never fire and `canScrollNext` would stay false against a row that does overflow.
  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    measure();
    scroller.addEventListener("scroll", measure, { passive: true });
    // Slides that resize as their images arrive, and a window resize, both move the snap points.
    const observer = new ResizeObserver(measure);
    observer.observe(scroller);
    for (const slide of Array.from(scroller.children)) observer.observe(slide);
    return () => {
      scroller.removeEventListener("scroll", measure);
      observer.disconnect();
    };
  }, [measure, slideCount]);

  // One slide per press, by the width of the slide now showing, so a row of fixed-width cards
  // pages by a card and the hero pages by a full panel.
  const scrollBy = useCallback((direction: -1 | 1) => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const slides = Array.from(scroller.children) as HTMLElement[];
    const current = slides[0];
    const step = current ? current.getBoundingClientRect().width + 16 : scroller.clientWidth;
    scroller.scrollBy({ left: step * direction, behavior: "smooth" });
  }, []);

  const value = useMemo<CarouselState>(
    () => ({
      scrollerRef,
      activeIndex,
      slideCount,
      canScrollPrev,
      canScrollNext,
      scrollBy,
      registerSlideCount: setSlideCount,
    }),
    [activeIndex, slideCount, canScrollPrev, canScrollNext, scrollBy],
  );

  return (
    <CarouselContext.Provider value={value}>
      <div
        data-slot="carousel"
        className={cn("flex flex-col items-center gap-2", className)}
        role="region"
        aria-roledescription="carousel"
        {...props}
      >
        {children}
      </div>
    </CarouselContext.Provider>
  );
}

/** The row the arrows sit either side of. */
function CarouselViewport({ className, children, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="carousel-viewport"
      className={cn("flex w-full items-center gap-2", className)}
      {...props}
    >
      {children}
    </div>
  );
}

function CarouselContent({ className, children, ...props }: React.ComponentProps<"div">) {
  const { scrollerRef, registerSlideCount } = useCarousel();
  const count = Array.isArray(children) ? children.flat().length : children ? 1 : 0;

  useEffect(() => {
    registerSlideCount(count);
  }, [count, registerSlideCount]);

  return (
    <div
      ref={scrollerRef}
      data-slot="carousel-content"
      className={cn(
        "flex min-w-0 flex-1 snap-x snap-mandatory gap-4 overflow-x-auto overflow-y-clip scroll-smooth [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  );
}

function CarouselItem({ className, ...props }: React.ComponentProps<"div">) {
  return (
    <div
      data-slot="carousel-item"
      role="group"
      aria-roledescription="slide"
      className={cn("min-w-0 shrink-0 snap-start", className)}
      {...props}
    />
  );
}

function CarouselPrevious({ className, ...props }: React.ComponentProps<"button">) {
  const { scrollBy, canScrollPrev } = useCarousel();
  return (
    <button
      type="button"
      data-slot="carousel-previous"
      aria-label="Previous slide"
      disabled={!canScrollPrev}
      onClick={() => scrollBy(-1)}
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-full border border-input bg-white/5 text-foreground shadow-xs transition-opacity hover:bg-white/10 disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <ArrowLeftIcon className="size-4" />
    </button>
  );
}

function CarouselNext({ className, ...props }: React.ComponentProps<"button">) {
  const { scrollBy, canScrollNext } = useCarousel();
  return (
    <button
      type="button"
      data-slot="carousel-next"
      aria-label="Next slide"
      disabled={!canScrollNext}
      onClick={() => scrollBy(1)}
      className={cn(
        "flex size-8 shrink-0 items-center justify-center rounded-full border border-input bg-white/5 text-foreground shadow-xs transition-opacity hover:bg-white/10 disabled:pointer-events-none disabled:opacity-50",
        className,
      )}
      {...props}
    >
      <ArrowRightIcon className="size-4" />
    </button>
  );
}

/**
 * The design's "Slide 1 of 5". A live region, so a screen reader hears the change that the
 * arrows caused — the slides themselves are only scrolled, not re-rendered.
 */
function CarouselStatus({ className, ...props }: React.ComponentProps<"p">) {
  const { activeIndex, slideCount } = useCarousel();
  if (slideCount < 2) return null;
  return (
    <p
      data-slot="carousel-status"
      aria-live="polite"
      className={cn("text-center text-sm text-muted-foreground", className)}
      {...props}
    >
      Slide {activeIndex + 1} of {slideCount}
    </p>
  );
}

export type { CarouselState };
export {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselStatus,
  CarouselViewport,
  useCarousel,
};
